import { useEffect, useState } from 'react';
import { Container, Header, Card, Icon, Loader, Dimmer, Image } from 'semantic-ui-react';
import { t } from './locales';

function InstancePicker() {
    const [instances, setInstances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        // listed=1: organisations hidden from the homepage stay out of this picker (they still
        // work — /i/<slug> resolves against the full list, see lib/tenant.js).
        fetch('https://api.selwynevents.com/api/v1/instances?listed=1', {
            headers: { 'X-Source-Host': window.location.host },
        })
            .then(r => r.json())
            .then(data => {
                const list = data.instances || data;
                setInstances(Array.isArray(list) ? list : []);
            })
            .catch(e => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    if (loading) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--colour_menu_main, #481b52)' }}>
                <Dimmer active inverted>
                    <Loader size="large">{t('common.loading')}</Loader>
                </Dimmer>
            </div>
        );
    }

    if (error || instances.length === 0) {
        return (
            <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'var(--colour_menu_main, #481b52)' }}>
                <Container textAlign="center">
                    <Icon name={error ? 'warning sign' : 'inbox'} size="huge" style={{ color: 'rgba(255,255,255,0.4)' }} />
                    <Header as="h2" inverted>
                        {error ? t('common.couldNotLoadEvents') : t('common.noEventsAvailable')}
                    </Header>
                    {error && <p style={{ color: 'rgba(255,255,255,0.7)' }}>{error}</p>}
                </Container>
            </div>
        );
    }

    return (
        <div style={{ minHeight: '100vh', padding: '60px 20px', backgroundColor: 'var(--colour_menu_main, #481b52)' }}>
            <Container>
                <div style={{ textAlign: 'center', marginBottom: 50 }}>
                    <Icon name="ticket" size="huge" style={{ color: 'rgba(255,255,255,0.5)', marginBottom: 20 }} />
                    <Header as="h1" inverted style={{ fontSize: 36, fontWeight: 300, marginTop: 0 }}>
                        {t('instancePicker.title')}
                    </Header>
                    <p style={{ fontSize: 18, color: 'rgba(255,255,255,0.7)' }}>
                        {t('instancePicker.subtitle')}
                    </p>
                </div>

                {/* Browse all events across all organisers */}
                <div style={{ marginBottom: 32 }}>
                    <Card
                        fluid
                        onClick={() => { window.location.href = '/directory'; }}
                        link
                        style={{
                            borderRadius: 12,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            background: 'rgba(255,255,255,0.12)',
                            border: '2px dashed rgba(255,255,255,0.3)',
                            boxShadow: 'none',
                        }}
                        onMouseEnter={e => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.18)';
                        }}
                        onMouseLeave={e => {
                            e.currentTarget.style.background = 'rgba(255,255,255,0.12)';
                        }}
                    >
                        <Card.Content style={{ textAlign: 'center', padding: '20px' }}>
                            <Icon name="globe" size="large" style={{ color: 'rgba(255,255,255,0.7)', marginBottom: 8 }} />
                            <Card.Header style={{ color: '#fff', fontSize: '1.1rem' }}>
                                {t('instancePicker.browseAllTitle')}
                            </Card.Header>
                            <Card.Description style={{ color: 'rgba(255,255,255,0.7)', marginTop: 4 }}>
                                {t('instancePicker.browseAllDescription')}
                            </Card.Description>
                        </Card.Content>
                    </Card>
                </div>

                <Card.Group centered stackable itemsPerRow={instances.length <= 3 ? instances.length : 3}>
                    {instances.map((instance) => (
                        <Card
                            key={instance.id || instance.slug}
                            onClick={() => { window.location.href = `/i/${instance.slug}/`; }}
                            link
                            style={{
                                borderRadius: 12,
                                overflow: 'hidden',
                                cursor: 'pointer',
                                borderTop: '4px solid var(--colour_heading_success, #a4cf58)',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                            }}
                            onMouseEnter={e => {
                                e.currentTarget.style.transform = 'translateY(-4px)';
                                e.currentTarget.style.boxShadow = '0 12px 40px rgba(0,0,0,0.35)';
                            }}
                            onMouseLeave={e => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '';
                            }}
                        >
                            {instance.logo && (
                                <div style={{ padding: 25, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9f9f9', minHeight: 100 }}>
                                    <Image src={instance.logo} style={{ maxHeight: 70, maxWidth: '80%', objectFit: 'contain' }} />
                                </div>
                            )}
                            <Card.Content>
                                <Card.Header style={{ color: 'var(--colour_menu_main, #481b52)' }}>
                                    {instance.name || instance.slug}
                                </Card.Header>
                                {instance.description && (
                                    <Card.Description style={{ marginTop: 8, color: '#666' }}>
                                        {instance.description}
                                    </Card.Description>
                                )}
                            </Card.Content>
                            <Card.Content extra style={{ color: 'var(--colour_menu_main, #481b52)' }}>
                                <Icon name="arrow right" style={{ color: 'var(--colour_heading_success, #a4cf58)' }} />
                                {t('instancePicker.browseEvents')}
                            </Card.Content>
                        </Card>
                    ))}
                </Card.Group>
            </Container>
        </div>
    );
}

export default InstancePicker;
